export type ThemePreference = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

export const THEME_STORAGE_KEY = 'myofade-theme'
/** Pre-rename key; read for migration only. */
const LEGACY_THEME_STORAGE_KEY = 'myoheat-theme'

const THEME_META = {
  light: '#F4F6F8',
  dark: '#0C0F12',
} as const

const listeners = new Set<() => void>()

function notify(): void {
  for (const listener of listeners) listener()
}

export function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system'
}

export function readThemePreference(storage: Pick<Storage, 'getItem'> = localStorage): ThemePreference {
  try {
    const raw = storage.getItem(THEME_STORAGE_KEY) ?? storage.getItem(LEGACY_THEME_STORAGE_KEY)
    if (isThemePreference(raw)) return raw
  } catch {
    /* private mode / blocked storage */
  }
  return 'system'
}

export function writeThemePreference(
  preference: ThemePreference,
  storage: Pick<Storage, 'setItem'> = localStorage,
): void {
  try {
    storage.setItem(THEME_STORAGE_KEY, preference)
  } catch {
    /* ignore */
  }
}

export function systemPrefersDark(media?: Pick<MediaQueryList, 'matches'> | null): boolean {
  if (media) return media.matches
  if (typeof window.matchMedia !== 'function') return true
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

export function resolveTheme(
  preference: ThemePreference,
  prefersDark = systemPrefersDark(),
): ResolvedTheme {
  if (preference === 'light') return 'light'
  if (preference === 'dark') return 'dark'
  return prefersDark ? 'dark' : 'light'
}

/** Apply resolved theme to `<html>` and theme-color meta. */
export function applyResolvedTheme(
  resolved: ResolvedTheme,
  root: HTMLElement = document.documentElement,
): void {
  root.dataset.theme = resolved
  root.style.colorScheme = resolved
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute('content', THEME_META[resolved])
}

export function applyThemePreference(preference: ThemePreference): ResolvedTheme {
  const resolved = resolveTheme(preference)
  applyResolvedTheme(resolved)
  notify()
  return resolved
}

export function setThemePreference(preference: ThemePreference): ResolvedTheme {
  writeThemePreference(preference)
  return applyThemePreference(preference)
}

export function getResolvedTheme(): ResolvedTheme {
  const attr = document.documentElement.dataset.theme
  if (attr === 'light' || attr === 'dark') return attr
  return resolveTheme(readThemePreference())
}

export function subscribeTheme(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Viewer / canvas background matching current resolved theme. */
export function viewerBackground(resolved: ResolvedTheme = getResolvedTheme()): string {
  return resolved === 'light' ? '#f4f6f8' : '#0c0f12'
}
